import { useEffect, useState } from "react";
import type { PayHandles, SaleTender } from "@shared/events";
import type { CurrencyCode, QrProvider } from "@shared/money";
import { formatMinor, providersForCurrency, quickTenderOptions } from "@shared/money";
import { Keypad } from "./Keypad";
import { changeDueMinor, isSufficientTender } from "./cart";
import { keypadDigitsToMinor } from "@shared/money";
import { buildPayLink, displayPayHandle } from "@shared/paylinks";

export const PROVIDER_LABELS: Record<QrProvider, string> = {
  cashapp: "Cash App",
  paypal: "PayPal",
  venmo: "Venmo",
};

/** Providers the vendor can actually be paid through: supported by the currency and configured (F-405). */
export function availableProviders(currency: CurrencyCode, handles: PayHandles): Array<{ provider: QrProvider; handle: string }> {
  return providersForCurrency(currency)
    .map((provider) => ({ provider, handle: handles[provider] ?? "" }))
    .filter((p) => p.handle.length > 0);
}

/** "Includes $1.50 tip" beside a total that has one (D-063). */
function TipNote(props: { tipMinor: number; currency: CurrencyCode; className?: string }) {
  if (props.tipMinor <= 0) return null;
  return (
    <div className={props.className ?? "sell__stat-note"} data-testid="tender-tip">
      Includes {formatMinor(props.tipMinor, props.currency)} tip
    </div>
  );
}

// ---------- Cash (F-404) ----------

function CashTender(props: {
  /** Amount due: goods plus any tip. */
  totalMinor: number;
  tipMinor: number;
  currency: CurrencyCode;
  onDone: (tender: Extract<SaleTender, { type: "cash" }>) => void;
  onBack: () => void;
}) {
  const [digits, setDigits] = useState("");
  const chips = quickTenderOptions(props.totalMinor, props.currency);
  const tendered = digits === "" ? 0 : keypadDigitsToMinor(digits);
  const enough = digits !== "" && isSufficientTender(props.totalMinor, tendered);
  const change = changeDueMinor(props.totalMinor, tendered);

  return (
    <section className="sell__sheet sell__sheet--fit" role="dialog" aria-modal="true" aria-label="Cash tender">
      <header className="sell__sheet-head">
        {/* The tip note sits beside the title here, not under the total: the sheet has no height to spare on
            short screens, and under the total it widened the figures until "Change due" broke (D-063). */}
        <div className="sell__sheet-titles">
          <h2 className="sell__sheet-title">Cash</h2>
          <TipNote tipMinor={props.tipMinor} currency={props.currency} className="sell__sheet-note" />
        </div>
        <button type="button" className="sell__btn sell__btn--quiet sell__sheet-close" onClick={props.onBack}>
          Back
        </button>
      </header>

      {/* Sized to the viewport so nothing scrolls on a phone, tablet or desktop window (D-059): the keypad
          takes whatever height is left, and from 600px landscape it moves beside the stats and chips. */}
      <div className="sell__sheet-body sell__sheet-body--fit">
        <div className="sell__cash">
          <div className="sell__cash-stats">
            <div className="sell__cash-figures">
              <div className="sell__stat">
                <div className="sell__stat-label">Total</div>
                <div className="sell__amount sell__amount--total" data-testid="tender-total">
                  {formatMinor(props.totalMinor, props.currency)}
                </div>
              </div>
              <div className="sell__stat">
                <div className="sell__stat-label">Received</div>
                <div className="sell__amount sell__amount--received" data-testid="cash-received">
                  {digits === "" ? "—" : formatMinor(tendered, props.currency)}
                </div>
              </div>
            </div>
            <div className="sell__stat sell__cash-change">
              <div className="sell__stat-label">Change due</div>
              <div className="sell__amount sell__amount--change" data-testid="change-due">
                {formatMinor(change, props.currency)}
              </div>
            </div>
          </div>

          <div className="sell__chips sell__cash-chips" role="group" aria-label="Quick tender">
            {chips.map((amount) => (
              <button
                key={amount}
                type="button"
                className="sell__chip"
                aria-pressed={digits !== "" && tendered === amount}
                onClick={() => setDigits(String(amount))}
              >
                {amount === props.totalMinor ? `Exact · ${formatMinor(amount, props.currency)}` : formatMinor(amount, props.currency)}
              </button>
            ))}
          </div>

          <Keypad className="sell__cash-keypad" digits={digits} onDigits={setDigits} />
        </div>
      </div>

      <footer className="sell__sheet-foot">
        <button
          type="button"
          className="sell__btn sell__btn--block sell__btn--tall"
          disabled={!enough}
          onClick={() => props.onDone({ type: "cash", tenderedMinor: tendered, changeMinor: change })}
        >
          Done
        </button>
      </footer>
    </section>
  );
}

// ---------- QR Pay (F-405, F-406) ----------

function QrTender(props: {
  /** Amount due: goods plus any tip, and what the pay link asks for. */
  totalMinor: number;
  tipMinor: number;
  currency: CurrencyCode;
  handles: PayHandles;
  registerName: string;
  onDone: (tender: Extract<SaleTender, { type: "qr" }>) => void;
  onBack: () => void;
}) {
  const options = availableProviders(props.currency, props.handles);
  const [picked, setPicked] = useState<QrProvider | null>(options.length === 1 ? (options[0]?.provider ?? null) : null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  const chosen = options.find((o) => o.provider === picked) ?? null;
  const link = chosen ? buildPayLink(chosen.provider, chosen.handle, props.totalMinor, props.currency, props.registerName) : null;
  const payee = chosen ? (displayPayHandle(chosen.provider, chosen.handle) ?? chosen.handle) : "";

  useEffect(() => {
    let live = true;
    setDataUrl(null);
    if (!link) return;
    // Loaded on demand: the grid must not pay for the QR encoder on first paint (U-008).
    void import("qrcode")
      .then((qr) => qr.toDataURL(link, { margin: 1, width: 512, errorCorrectionLevel: "M" }))
      .then((url) => {
        if (live) setDataUrl(url);
      })
      .catch(() => {
        /* the pay link itself is shown as the fallback */
      });
    return () => {
      live = false;
    };
  }, [link]);

  return (
    <section className="sell__sheet" role="dialog" aria-modal="true" aria-label="QR Pay">
      <header className="sell__sheet-head">
        <h2 className="sell__sheet-title">QR Pay</h2>
        <button
          type="button"
          className="sell__btn sell__btn--quiet sell__sheet-close"
          onClick={() => (chosen && options.length > 1 ? setPicked(null) : props.onBack())}
        >
          {chosen && options.length > 1 ? "Back" : "Cancel"}
        </button>
      </header>

      <div className="sell__sheet-body">
        <div className="sell__stat">
          <div className="sell__stat-label">Amount</div>
          <div className="sell__amount sell__amount--total">{formatMinor(props.totalMinor, props.currency)}</div>
          <TipNote tipMinor={props.tipMinor} currency={props.currency} />
        </div>

        {options.length === 0 ? (
          <p className="sell__warn">
            No payment handles for {props.currency}. Add a Cash App, PayPal, or Venmo handle in Setup.
          </p>
        ) : !chosen ? (
          <div className="sell__providers">
            {options.map((o) => (
              <button
                key={o.provider}
                type="button"
                className="sell__btn sell__btn--block sell__btn--tall sell__btn--quiet"
                onClick={() => setPicked(o.provider)}
              >
                {PROVIDER_LABELS[o.provider]}
                <span className="sell__provider-handle">{displayPayHandle(o.provider, o.handle) ?? o.handle}</span>
              </button>
            ))}
          </div>
        ) : (
          <div>
            {/* The payee is the biggest thing on the sheet so the customer, the cashier and anyone watching can
                check it against the recipient the customer's app shows before paying (D-058). */}
            <div className="sell__payee" data-testid="qr-payee">
              <div className="sell__stat-label">Pay to · {PROVIDER_LABELS[chosen.provider]}</div>
              <div className="sell__payee-handle">{payee}</div>
              {props.registerName ? <div className="sell__payee-register">{props.registerName}</div> : null}
            </div>
            {dataUrl ? (
              <img
                className="sell__qr"
                src={dataUrl}
                alt={`${PROVIDER_LABELS[chosen.provider]} payment code: pay ${payee} ${formatMinor(props.totalMinor, props.currency)}`}
              />
            ) : null}
            {link ? (
              <p className="sell__payee-check">
                Customer: before you pay, check your app says you are paying <strong>{payee}</strong>.
              </p>
            ) : null}
            {link ? (
              <span className="sell__qr-link">{link}</span>
            ) : (
              <p className="sell__warn">This provider cannot take {props.currency}. Choose another.</p>
            )}
            <p className="sell__note">WebCashier does not check the payment. Confirm it in your provider app first.</p>
          </div>
        )}
      </div>

      {chosen ? (
        <footer className="sell__sheet-foot">
          <button
            type="button"
            className="sell__btn sell__btn--block sell__btn--tall"
            onClick={() => props.onDone({ type: "qr", provider: chosen.provider })}
          >
            Payment received
          </button>
        </footer>
      ) : null}
    </section>
  );
}

// ---------- Tender chooser (F-403) ----------

export function TenderScreen(props: {
  /** Amount due: goods plus any tip (D-063). */
  totalMinor: number;
  /** The tip inside `totalMinor`, shown as a note; 0 or absent for none. */
  tipMinor?: number;
  currency: CurrencyCode;
  handles: PayHandles;
  registerName: string;
  onComplete: (tender: SaleTender) => void;
  onCancel: () => void;
}) {
  // With no QR Pay handle configured at all, cash is the only tender, so the Cash/QR chooser is pointless —
  // go straight to the cash keypad (D-051). "Back" from there then cancels the whole sale, not a phantom
  // step. A handle that exists but can't take this currency still shows the chooser (QR Pay explains it).
  const hasPayHandles = Object.values(props.handles).some((h) => typeof h === "string" && h.trim().length > 0);
  const [mode, setMode] = useState<"choose" | "cash" | "qr">(hasPayHandles ? "choose" : "cash");
  const tipMinor = props.tipMinor ?? 0;

  if (mode === "cash") {
    return (
      <CashTender
        totalMinor={props.totalMinor}
        tipMinor={tipMinor}
        currency={props.currency}
        onDone={props.onComplete}
        onBack={hasPayHandles ? () => setMode("choose") : props.onCancel}
      />
    );
  }
  if (mode === "qr") {
    return (
      <QrTender
        totalMinor={props.totalMinor}
        tipMinor={tipMinor}
        currency={props.currency}
        handles={props.handles}
        registerName={props.registerName}
        onDone={props.onComplete}
        onBack={() => setMode("choose")}
      />
    );
  }

  return (
    <section className="sell__sheet" role="dialog" aria-modal="true" aria-label="Tender">
      <header className="sell__sheet-head">
        <h2 className="sell__sheet-title">Checkout</h2>
        <button type="button" className="sell__btn sell__btn--quiet sell__sheet-close" onClick={props.onCancel}>
          Cancel
        </button>
      </header>
      <div className="sell__sheet-body">
        <div className="sell__stat">
          <div className="sell__stat-label">Total</div>
          <div className="sell__amount sell__amount--change" data-testid="charge-total">
            {formatMinor(props.totalMinor, props.currency)}
          </div>
          <TipNote tipMinor={tipMinor} currency={props.currency} />
        </div>
        <div className="sell__tender-choices">
          <button type="button" className="sell__btn sell__btn--block sell__btn--tall" onClick={() => setMode("cash")}>
            Cash
          </button>
          <button type="button" className="sell__btn sell__btn--block sell__btn--tall" onClick={() => setMode("qr")}>
            QR Pay
          </button>
        </div>
      </div>
    </section>
  );
}
